import { userManagementApi } from './userManagementService';

// Types for Services
export interface Service {
  id: string;
  name: string;
  code: string;
  description: string;
  category: string;
  duration: number; // in minutes
  basePrice: number;
  vatRate: number;
  materials: ServiceMaterial[];
  labRequired: boolean;
  rules: ServiceRule[];
  portalVisibility: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  clinicId: string;
}

export interface ServiceMaterial {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  cost: number;
}

export interface ServiceRule {
  id: string;
  type: 'age_limit' | 'weight_limit' | 'species_restriction' | 'vaccination_required' | 'custom';
  value: string;
  description: string;
}

export interface ServicePackage {
  id: string;
  name: string;
  description: string;
  services: string[]; // Service IDs
  discount: number; // percentage
  price: number;
  validityDays: number;
  isActive: boolean;
  clinicId: string;
}

export interface ServiceFilters {
  search?: string;
  category?: string;
  priceRange?: [number, number];
  duration?: number;
  labRequired?: boolean;
  portalVisibility?: boolean;
  isActive?: boolean;
  clinicId?: string;
}

export interface CreateServiceData {
  name: string;
  code: string;
  description: string;
  category: string;
  duration: number;
  basePrice: number;
  vatRate: number;
  materials: Omit<ServiceMaterial, 'id'>[];
  labRequired: boolean;
  rules: Omit<ServiceRule, 'id'>[];
  portalVisibility: boolean;
  clinicId: string;
}

export interface UpdateServiceData {
  name?: string;
  code?: string;
  description?: string;
  category?: string;
  duration?: number;
  basePrice?: number;
  vatRate?: number;
  materials?: Omit<ServiceMaterial, 'id'>[];
  labRequired?: boolean;
  rules?: Omit<ServiceRule, 'id'>[];
  portalVisibility?: boolean;
  isActive?: boolean;
}

export interface ServiceStats {
  totalServices: number;
  activeServices: number;
  categories: { [key: string]: number };
  averagePrice: number;
  totalRevenue: number;
  popularServices: { serviceId: string; name: string; count: number }[];
}

class ServicesService {
  private baseUrl = '/services';

  async getServices(filters: ServiceFilters = {}): Promise<{ services: Service[]; total: number; page: number; limit: number; stats: ServiceStats }> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.priceRange) {
        params.append('priceMin', filters.priceRange[0].toString());
        params.append('priceMax', filters.priceRange[1].toString());
      }
      if (filters.duration) params.append('duration', filters.duration.toString());
      if (filters.labRequired !== undefined) params.append('labRequired', filters.labRequired.toString());
      if (filters.portalVisibility !== undefined) params.append('portalVisibility', filters.portalVisibility.toString());
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
      if (filters.clinicId) params.append('clinicId', filters.clinicId);

      const response = await userManagementApi.get(`${this.baseUrl}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching services:', error);
      throw error;
    }
  }

  async getServiceById(id: string): Promise<Service> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching service:', error);
      throw error;
    }
  }

  async createService(data: CreateServiceData): Promise<Service> {
    try {
      const response = await userManagementApi.post(this.baseUrl, data);
      return response.data;
    } catch (error) {
      console.error('Error creating service:', error);
      throw error;
    }
  }

  async updateService(id: string, data: UpdateServiceData): Promise<Service> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating service:', error);
      throw error;
    }
  }

  async deleteService(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Error deleting service:', error);
      throw error;
    }
  }

  async getServicePackages(clinicId: string): Promise<ServicePackage[]> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/packages?clinicId=${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching service packages:', error);
      throw error;
    }
  }

  async createServicePackage(data: Omit<ServicePackage, 'id' | 'clinicId'> & { clinicId: string }): Promise<ServicePackage> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/packages`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating service package:', error);
      throw error;
    }
  }

  async updateServicePackage(id: string, data: Partial<Omit<ServicePackage, 'id' | 'clinicId'>>): Promise<ServicePackage> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/packages/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating service package:', error);
      throw error;
    }
  }

  async deleteServicePackage(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/packages/${id}`);
    } catch (error) {
      console.error('Error deleting service package:', error);
      throw error;
    }
  }

  async getServiceStats(clinicId: string): Promise<ServiceStats> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/stats?clinicId=${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching service stats:', error);
      throw error;
    }
  }

  async exportServices(filters: ServiceFilters = {}): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await userManagementApi.get(`${this.baseUrl}/export?${params.toString()}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting services:', error);
      throw error;
    }
  }
}

export const servicesService = new ServicesService();
